import * as React from "react";
import {useEffect, useState} from "react";
import {useLeadStore} from "../store/Lead.store";
import {InputControlComponent} from "../../form/inputControl.component";
import {RadioButtonListControlComponent} from "../../form/radioButtonListControl.component";
import {RadioFieldListControlComponent} from "../../form/radioFieldListControl.component";
import {observer} from "mobx-react-lite";
import {AutosuggestComponent} from "../../form/autosuggest.component";
import './order-type-selection-step.scss';
import {ReactComponent as OrderTypeSolarBattery} from "../icons/package_solar_battery.svg";
import {ReactComponent as OrderTypeBattery} from "../icons/package_battery.svg";
import {StepActions} from "../components/step-actions.component";
import useBelfiusTheme from "../theme/ThemeHooks";
import {InputHelpText} from "../../form/InputHelpText";

const SVGS = {
    "package": {
        "solar+battery": OrderTypeSolarBattery, "solar": OrderTypeSolarBattery, "battery": OrderTypeBattery
    }
};

const HomeStatusSelection = observer( () => {
    const store = useLeadStore();
    const [homeStatusOptions, setHomeStatusOptions] = useState( [] );
    let {themeClassName} = useBelfiusTheme();

    useEffect( () => {
        setHomeStatusOptions( [{
            text: store.translate( 'leadgeneration.steps.orderTypeSelection.homeStatus.existing' ),
            value: 'existing',
            selected: store.quote.homeStatus === 'existing',
        }, {
            text: store.translate( 'leadgeneration.steps.orderTypeSelection.homeStatus.new' ), value: 'new', selected: store.quote.homeStatus === 'new',
        }] );
    }, [store.quote.homeStatus] );

    return (<React.Fragment>
        <h2 className={themeClassName}>{store.translate( 'leadgeneration.steps.orderTypeSelection.homeStatusTitle' )} *</h2>
        <div className={"mb-3"}>
            <RadioButtonListControlComponent name={"status"}
                                             onChange={( value ) => {
                                                 store.setHomeStatus( value );
                                             }}
                                             theme={themeClassName}
                                             options={homeStatusOptions}/>
        </div>
    </React.Fragment>);
} );

const PackageSelection = observer( () => {
    const store = useLeadStore();
    let {themeClassName} = useBelfiusTheme();

    const availablePackages = store.availablePackages.slice()
            .map( pack => {
                const Icon = SVGS.package[pack.value];
                return {...pack, key: pack.value, imageUrl: <Icon/>}
            } );

    if ( availablePackages.length < 1 ) {
        return <React.Fragment/>
    }

    return (<React.Fragment>
        <h2 className={themeClassName}>{store.translate( 'leadgeneration.steps.orderTypeSelection.interestedTitle' )} *</h2>

        <div className={"mb-4"}>
            <RadioFieldListControlComponent name={"package"}
                                            onChange={( value ) => {
                                                store.setChosenPackage( value );
                                            }}
                                            theme={themeClassName}
                                            options={availablePackages}/>
        </div>
    </React.Fragment>);
} );

const YearlyUsageEntry = observer( () => {
    const store = useLeadStore();
    let {themeClassName} = useBelfiusTheme();

    if ( store.quote.homeStatus === 'new' ) {
        return (<React.Fragment/>);
    }

    return (<React.Fragment>
        <h2 className={themeClassName}>{store.translate( 'leadgeneration.steps.orderTypeSelection.electricityUsageTitle' )}</h2>
        <div className={"d-flex mb-3 w-100 justify-content-left"}>
            <div className={"form-group"}>
                <InputControlComponent type={'number'}
                                       theme={themeClassName}
                                       currentValue={store.quote.yearlyElectricityUsage}
                                       describedBy={'yearlyUsage'}
                                       appendText={'kWh'}
                                       onChange={( e ) => {
                                           store.setYearlyElectricityUsage( e.target.valueAsNumber );
                                       }}/>
            </div>

        </div>
    </React.Fragment>)
} );

const ContactForm = observer( () => {
    const store = useLeadStore();
    let {themeClassName} = useBelfiusTheme();

    const streetInputHelpText = <InputHelpText text={store.translate( 'leadgeneration.steps.orderTypeSelection.street.help' )} id={'streetHelp'}/>;
    const vatNumberInputHelpText = <InputHelpText text={store.translate( 'leadgeneration.steps.orderTypeSelection.vatNumber.help' )} id={'vatNumberHelp'}/>;

    return (<React.Fragment>
        <h2 className={themeClassName}>{store.translate( 'leadgeneration.steps.orderTypeSelection.contactInformationTitle' )} *</h2>
        <div className="d-flex justify-content-center">
            <div className="order-contact-form">
                <div className={"row form-group"}>
                    <div className={'col-md-6'}>
                        <InputControlComponent required={true}
                                               type={'text'}
                                               placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.firstName.placeholder' )}
                                               currentValue={store.contact.firstName}
                                               theme={themeClassName}
                                               onChange={( e ) => {
                                                   store.setFirstName( e.target.value );
                                               }}/>
                    </div>
                    <div className={'col-md-6'}>
                        <InputControlComponent required={true}
                                               type={'text'}
                                               placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.lastName.placeholder' )}
                                               currentValue={store.contact.lastName}
                                               theme={themeClassName}
                                               onChange={( e ) => {
                                                   store.setLastName( e.target.value );
                                               }}/>
                    </div>
                </div>
                <div className={"form-group"}>
                    <InputControlComponent required={true}
                                           type={'email'}
                                           placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.email.placeholder' )}
                                           currentValue={store.contact.email}
                                           theme={themeClassName}
                                           onChange={( e ) => {
                                               store.setEmail( e.target.value );
                                           }}/>
                </div>

                <div className={"form-group"}>
                    <InputControlComponent required={true}
                                           type={'tel'}
                                           placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.telephoneNumber.placeholder' )}
                                           currentValue={store.contact.phoneNumber}
                                           onChange={( e ) => {
                                               store.setPhoneNumber( e.target.value );
                                           }}
                                           theme={themeClassName}
                    />
                </div>

                <div className={"form-group"}>
                    <InputControlComponent required={false}
                                           placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.vatNumber.placeholder' )}
                                           currentValue={store.contact.vatNumber}
                                           onChange={( e ) => {
                                               store.setVatNumber( e.target.value );
                                           }}
                                           inputHelpText={vatNumberInputHelpText}
                                           theme={themeClassName}
                    />
                </div>
            </div>
        </div>
        <h2 className={themeClassName}>{store.translate( 'leadgeneration.steps.orderTypeSelection.addressInformationTitle' )} *</h2>
        <div className="d-flex justify-content-center">
            <div className="order-contact-form">
                <div className={"row form-group"}>
                    <div className={'col-md-6'}>
                        <InputControlComponent required={true}
                                               type={'text'}
                                               placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.street.placeholder' )}
                                               currentValue={store.contact.street}
                                               theme={themeClassName}
                                               inputHelpText={streetInputHelpText}
                                               describedBy={"streetHelp"}
                                               maxLength={30}
                                               onChange={( e ) => {
                                                   store.setStreet( e.target.value );
                                               }}/>
                    </div>
                    <div className={'col-md-3'}>
                        <InputControlComponent required={true}
                                               type={'text'}
                                               placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.houseNumber.placeholder' )}
                                               currentValue={store.contact.number}
                                               theme={themeClassName}
                                               onChange={( e ) => {
                                                   store.setNumber( e.target.value );
                                               }}/>
                    </div>
                    <div className={'col-md-3'}>
                        <InputControlComponent required={false}
                                               type={'text'}
                                               placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.boxNumber.placeholder' )}
                                               currentValue={store.contact.numberAddition}
                                               theme={themeClassName}
                                               onChange={( e ) => {
                                                   store.setNumberAddition( e.target.value );
                                               }}/>
                    </div>
                </div>
                <div className={'form-group'}>
                    <AutosuggestComponent
                            placeholder={store.translate( 'leadgeneration.steps.orderTypeSelection.postalCode.placeholder' )}
                            onChange={( value ) => {
                                let simpleValue;
                                if ( value !== "" ) {
                                    simpleValue = value.split( '-' )[0].trim();
                                }
                                store.setPostalCode( simpleValue, value );
                            }}
                            theme={themeClassName}
                    />
                </div>
            </div>
        </div>
    </React.Fragment>)
} );

const ProceedButton = observer( () => {
    const store = useLeadStore();
    return <StepActions showCreateAppointment={true}
                        hideNextStep={!store.configuration.showAppointmentButton}
                        createAppointmentTitle={store.configuration.stepOneAppointmentButtonText}
                        nextStepTitle={store.configuration.stepOneNextButtonText}
    />;
} );

export const OrderTypeSelectionStep = () => {
    return (<div>
        <ContactForm/>
        <HomeStatusSelection/>
        {/*<PackageSelection/>*/}
        <YearlyUsageEntry/>
        <ProceedButton/>
    </div>)
};
